import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api-client';
import { classNames, debounce } from '@/lib/utils';
import type { Store } from '@/types';

export function Home() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const [stores, setStores] = useState<Store[]>([]);
    const [filteredStores, setFilteredStores] = useState<Store[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Carregar lojas
    useEffect(() => {
        async function loadStores() {
            setIsLoading(true);
            const response = await api.get<Store[]>('/stores');
            if (response.success && response.data) {
                setStores(response.data);
                setFilteredStores(response.data);
            }
            setIsLoading(false);
        }
        loadStores();
    }, []);

    // Filtrar lojas
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredStores(stores);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = stores.filter(store =>
            store.name.toLowerCase().includes(query) ||
            store.city?.toLowerCase().includes(query) ||
            store.code?.toLowerCase().includes(query)
        );
        setFilteredStores(filtered);
    }, [searchQuery, stores]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleStoreClick = (storeId: string) => {
        navigate(`/audit/${storeId}`);
    };

    return (
        <div className="min-h-screen bg-page-bg pb-20">
            {/* Header */}
            <header className="bg-brand-header text-content-inverse sticky top-0 z-10">
                <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
                    <div>
                        <h1 className="text-lg font-semibold">Share of Shelf</h1>
                        <p className="text-sm opacity-80">Olá, {user?.name || 'Usuário'}</p>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        title="Sair"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                        </svg>
                    </button>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-lg mx-auto px-4 py-6">
                {/* Search */}
                <div className="mb-6">
                    <div className="relative">
                        <svg
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-content-muted"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                        <input
                            type="text"
                            placeholder="Buscar loja..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-ui-border bg-white
                         text-content-body placeholder:text-content-muted
                         focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                        />
                    </div>
                </div>

                {/* Title */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-content-title">
                        Suas Lojas
                    </h2>
                    <span className="text-sm text-content-muted">
                        {filteredStores.length} {filteredStores.length === 1 ? 'loja' : 'lojas'}
                    </span>
                </div>

                {/* Loading */}
                {isLoading && (
                    <div className="flex justify-center py-12">
                        <div className="w-10 h-10 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                )}

                {/* Store List */}
                {!isLoading && (
                    <div className="space-y-3">
                        {filteredStores.map((store) => (
                            <Card
                                key={store.id}
                                clickable
                                onClick={() => handleStoreClick(store.id)}
                            >
                                <CardContent>
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                                                {store.name}
                                                {store.hasBacklight && (
                                                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-status-warning/20 text-status-warning rounded-full">
                                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                                                        </svg>
                                                        Backlight
                                                    </span>
                                                )}
                                            </CardTitle>
                                            <CardDescription className="flex items-center gap-1">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                {store.city}, {store.state}
                                            </CardDescription>
                                            {store.address && (
                                                <p className="text-xs text-content-muted mt-1">
                                                    {store.address}
                                                </p>
                                            )}
                                        </div>
                                        <svg
                                            className="w-5 h-5 text-content-muted flex-shrink-0"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 5l7 7-7 7"
                                            />
                                        </svg>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Empty state */}
                {!isLoading && filteredStores.length === 0 && (
                    <div className="text-center py-12">
                        <svg
                            className="w-16 h-16 text-content-muted mx-auto mb-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                        </svg>
                        <p className="text-content-muted">
                            {searchQuery ? 'Nenhuma loja encontrada' : 'Nenhuma loja disponível'}
                        </p>
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="mt-2 text-brand-primary text-sm font-medium"
                            >
                                Limpar busca
                            </button>
                        )}
                    </div>
                )}
            </main>

            {/* Bottom Navigation */}
            <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-ui-border safe-area-pb">
                <div className="max-w-lg mx-auto flex">
                    <button className="flex-1 py-4 flex flex-col items-center text-brand-primary">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        <span className="text-xs mt-1 font-medium">Início</span>
                    </button>
                    <button
                        className="flex-1 py-4 flex flex-col items-center text-content-muted"
                        onClick={() => navigate('/dashboard')}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <span className="text-xs mt-1">Dashboard</span>
                    </button>
                </div>
            </nav>
        </div>
    );
}

export default Home;
